import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getProductData } from '../../api/getData';

// --- Helper Icon Components for Clarity ---

// Icon for "included" features (from the new template)
const CheckIcon = () => (
  <svg
    className="h-6 w-6 text-green-500"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.75 12L10.58 14.83L16.25 9.17004"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Icon for "not included" features (from your original code)
const CrossIcon = () => (
  <svg
    className="h-6 w-6 text-red-500"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.16998 14.83L14.83 9.17004"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.83 14.83L9.16998 9.17004"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function FeatureCompare2() {
  const [priceToggled, setPriceToggled] = React.useState(0);

  const getProducts = async () => {
    const data = await getProductData();
    // Assuming the API returns an array. Adjust if it's an object.
    return data;
  };

  const { data: products, status } = useQuery('products', getProducts);

  if (status === 'loading') {
    return <div className="text-center p-10">در حال بارگزاری...</div>;
  }
  if (status === 'error' || !products) {
    return <div className="text-center p-10">خطا در بارگزاری اطلاعات</div>;
  }

  const togglePrice = (index) => {
    setPriceToggled(index);
  };

  return (
    <main className="relative min-h-screen flex flex-col justify-center bg-white overflow-hidden">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-12">
        <h2 className="font-bold text-center text-3xl mb-8">
          شرایط و هزینه دوره های کاهش وزن و تناسب اندام
        </h2>

        {/* --- Your Price Toggle Switch --- */}
        <div className="flex justify-center mb-8">
          <label
            dir="rtl"
            className="inline-flex items-center me-5 cursor-pointer">
            <span className="ms-3 m-2 text-sm font-medium text-gray-900">
              کاربران خارج از ایران (یورو)
            </span>
            <input
              type="checkbox"
              className="sr-only peer"
              onChange={(e) => togglePrice(e.target.checked ? 1 : 0)}
            />
            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-300 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ms-3 m-2 text-sm font-medium text-gray-900">
              کاربران داخل ایران (تومان)
            </span>
          </label>
        </div>

        {/* --- New Pricing Cards Grid --- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.map((product, i) => (
            <div
              key={i}
              className={`rounded-[30px] md:rounded-[36px] bg-[#FAFAFA] overflow-hidden border-[1px] border-gray-200 p-6 relative ${
                product.status === 'noSeat' ? 'opacity-60' : '' // Dim card if no seats
              }`}>
              {/* "No Seat" Overlay */}
              {product.status === 'noSeat' && (
                <div className="absolute inset-0 bg-gray-300 bg-opacity-75 flex items-center justify-center z-20">
                  <span className="text-black font-bold text-lg p-3 bg-white/80 backdrop-blur-sm rounded-lg shadow-md">
                    ظرفیت تکمیل شد
                  </span>
                </div>
              )}
              {product.status === 'notActive' && (
                <div className="absolute inset-0 bg-gray-300 bg-opacity-75 flex items-center justify-center z-20">
                  <span className="text-black font-bold text-lg p-3 bg-white/80 backdrop-blur-sm rounded-lg shadow-md">
                    دوره منقضی شده است
                  </span>
                </div>
              )}

              <div className="flex flex-col flex-1 justify-between h-full space-y-5">
                {/* Top Section: Name and Price */}
                <div>
                  <div className="text-xl md:text-2xl font-bold text-gray-900">
                    <span>{product.name.displayName}</span>
                  </div>

                  {/* --- Price Section with Toggling Logic --- */}
                  <div className="pt-5 text-gray-500 font-medium text-base space-y-1 min-h-[60px]">
                    {priceToggled === 0 && ( // Dollar Price
                      <div className="flex items-end space-x-2">
                        <div className="flex items-baseline">
                          <span className="text-2xl md:text-3xl font-bold text-gray-900">
                            {product.isDiscounted
                              ? product.price.discountedPrice
                              : product.price.price}
                          </span>
                          <span className="text-gray-900">
                            {product.price.priceSign}
                          </span>
                        </div>
                        {product.isDiscounted && (
                          <span className="line-through text-gray-400">
                            {product.price.price}
                            {product.price.priceSign}
                          </span>
                        )}
                      </div>
                    )}
                    {priceToggled === 1 && ( // Rial Price
                      <div dir="rtl" className="flex flex-col items-end">
                        <div className="flex items-baseline">
                          <span className="text-2xl md:text-3xl font-bold text-gray-900">
                            {product.isDiscounted
                              ? product.priceRial.dispayDiscound
                              : product.priceRial.displayPrice}
                          </span>
                          <span className="mr-1">تومان</span>
                        </div>
                        {product.isDiscounted && (
                          <span className="line-through text-gray-400 mt-1">
                            {product.priceRial.displayPrice} تومان
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* --- Features List --- */}
                  <ul className="space-y-2 pt-8">
                    {product.features.map((feature, index) => (
                      <li
                        key={index}
                        dir="rtl"
                        className="flex items-center font-medium space-x-2 text-black">
                        <span className="flex-1 text-right text-sm">
                          {feature.value}
                        </span>
                        {feature.status ? <CheckIcon /> : <CrossIcon />}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bottom Section: CTA Button */}
                <div className="pt-2">
                  <Link
                    to={product.status === 'noSeat' ? '#' : `${product.href}`}
                    state={
                      product.status !== 'noSeat'
                        ? {
                            data: {
                              name: product.name.displayName,
                              price: product.price.price,
                              priceSign: product.price.priceSign,
                              discountedPrice: product.price.discountedPrice,
                              priceRial: product.priceRial.price,
                            },
                          }
                        : null
                    }
                    aria-disabled={product.status === 'noSeat'}
                    onClick={(e) =>
                      product.status === 'noSeat' && e.preventDefault()
                    }
                    className={`
                      appearance-none w-full inline-flex items-center justify-center group space-x-2.5 bg-black text-white py-4 px-5 rounded-2xl
                      ${
                        product.status === 'noSeat'
                          ? 'cursor-not-allowed bg-gray-500'
                          : 'hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer'
                      }
                    `}>
                    <span className="w-full font-semibold text-base text-center">
                      {product.status === 'noSeat'
                        ? 'ظرفیت تکمیل'
                        : `انتخاب ${product.name.displayName}`}
                    </span>
                    <svg
                      className="inline-block h-6"
                      viewBox="0 0 24 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M3 12.4999H21L14 19.4999M14 5.5L18 9.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"></path>
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

export default FeatureCompare2;
